import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Per-page structured data. The sitewide AutomotiveBusiness (LocalBusiness) schema
// lives in public/index.html; everything here must describe visible page content.

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://legendconversion.com/#organization",
  "name": "Legend Conversion Corp",
  "alternateName": "Legend Conversion",
  "url": "https://legendconversion.com",
  "logo": "https://legendconversion.com/logo.png",
  "description": "Legend Conversion Corp builds factory-direct custom luxury van conversions in Alvarado, Texas. Founded in 2023 on over 31 years of conversion industry experience.",
  "foundingDate": "2023",
  "founder": {
    "@type": "Person",
    "name": "Kent Ellington"
  },
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "4209 Longhorn Dr",
    "addressLocality": "Alvarado",
    "addressRegion": "TX",
    "postalCode": "76009",
    "addressCountry": "US"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+1-682-369-1500",
    "contactType": "sales",
    "availableLanguage": "English"
  }
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://legendconversion.com/#website",
  "name": "Legend Conversion",
  "url": "https://legendconversion.com",
  "description": "Factory-direct custom luxury van conversions built in Alvarado, Texas. Executive limos, family travel vans, party vans, and commercial shuttles.",
  "publisher": { "@id": "https://legendconversion.com/#organization" }
};

// FAQPage schema — injected ONLY on /faq where these Q&As are visible.
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How long does it take to build a custom van?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Most builds take 8-16 weeks depending on complexity and the current production queue. We confirm your production window in writing before the build begins."
      }
    },
    {
      "@type": "Question",
      "name": "What warranty do you offer on your conversions?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "All Legend Conversion builds come with a comprehensive 3-Year, 36,000-Mile Warranty covering all conversion work, upholstery, electrical systems, and mechanical components."
      }
    },
    {
      "@type": "Question",
      "name": "Can I customize a van I already own?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes! We can convert your existing van. We'll assess your vehicle and provide a custom quote based on your specifications."
      }
    },
    {
      "@type": "Question",
      "name": "How is pricing determined?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Every build is unique, so we provide personalized itemized quotes. Call us at (682) 369-1500 or use our Build Your Van configurator to select your options and request a custom quote."
      }
    },
    {
      "@type": "Question",
      "name": "Where is Legend Conversion located?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Legend Conversion Corp is located at 4209 Longhorn Dr, Alvarado, TX 76009. We serve customers nationwide and can arrange shipping to any location in the United States."
      }
    },
    {
      "@type": "Question",
      "name": "What makes Legend Conversion different from other custom van builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "With over 31 years of experience in the RV and conversion industry, founder Kent Ellington brings unmatched expertise. We're a family-owned company selling factory-direct — no dealership layer — with meticulous attention to detail."
      }
    }
  ]
};

const ROUTE_NAMES = {
  '/': 'Home',
  '/models': 'Models',
  '/showroom': 'Virtual Showroom',
  '/build': 'Build Your Van',
  '/gallery': 'Gallery',
  '/blog': 'Blog',
  '/about': 'About Us',
  '/testimonials': 'Testimonials',
  '/faq': 'FAQ',
  '/contact': 'Contact',
  '/compare': 'Compare Models',
  '/luxury-van-conversions': 'Luxury Van Conversions',
  '/party-van-conversions': 'Party Van Conversions',
  '/mercedes-sprinter-van-conversions': 'Mercedes Sprinter Van Conversions',
  '/executive-limo-van-conversions': 'Executive & Limo Van Conversions',
  '/family-travel-van-conversions': 'Family Travel Van Conversions',
  '/shuttle-van-conversions': 'Shuttle Van Conversions',
  '/texas-van-conversion-company': 'Texas Van Conversion Company',
  '/dallas-fort-worth-van-conversions': 'Dallas-Fort Worth Van Conversions',
  '/privacy': 'Privacy Policy',
  '/terms': 'Terms of Service',
};

const buildBreadcrumbSchema = (pathname) => {
  const crumbs = [{ name: 'Home', url: '/' }];
  if (pathname !== '/') {
    // Blog posts inject their own breadcrumb
    if (pathname.startsWith('/blog/') && pathname !== '/blog') return null;
    const routeName = ROUTE_NAMES[pathname];
    if (routeName) {
      crumbs.push({ name: routeName });
    } else {
      const segments = pathname.split('/').filter(Boolean);
      let path = '';
      segments.forEach((seg, i) => {
        path += `/${seg}`;
        const name = ROUTE_NAMES[path] || seg.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        crumbs.push(i < segments.length - 1 ? { name, url: path } : { name });
      });
    }
  }
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": crumbs.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      ...(item.url ? { "item": `${window.location.origin}${item.url}` } : {}),
    })),
  };
};

const SchemaMarkup = () => {
  const location = useLocation();

  useEffect(() => {
    const schemas = [organizationSchema, websiteSchema];
    if (location.pathname === '/faq') schemas.push(faqSchema);

    const scripts = schemas.map((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `schema-${index}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      return script;
    });

    const breadcrumbSchema = buildBreadcrumbSchema(location.pathname);
    if (breadcrumbSchema) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'schema-breadcrumb';
      script.textContent = JSON.stringify(breadcrumbSchema);
      document.head.appendChild(script);
      scripts.push(script);
    }

    return () => scripts.forEach((s) => s.remove());
  }, [location.pathname]);

  return null;
};

export default SchemaMarkup;
